// EditTask — modal-embedded form for updating a Task row.
// Refactored 2026-05-05 (Phase 3.6, Class P) — header onto ModalFormHeader.
// Preserves: PDF thumbnail re-gen, V-01 auto-match drawing + drift dot,
// rejection-reasons Autocomplete, status-conditional Comment, fullscreen modal,
// startedAt/completedAt read-only DateTimePickers.

import { useEffect, useMemo, useState } from "react";
import { FIELD_SX } from '../WeldAssure/BrandedForm';
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AssignmentTurnedInOutlined,
  PictureAsPdfOutlined,
  CloseRounded,
} from "@mui/icons-material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import CTextField from "../CTextField/CTextField";
import CSelect from "../CSelect/CSelect";
import { updateTasks } from "../../feature/tasksSlice";
import { isPdfUrl } from "../../utils/isPdfUrl";
import { AuthedImage, AuthedObject } from "../AuthedMedia/AuthedMedia";
import { getDrawingForTask } from "../../utils/documentsApi";
import prettifyDocumentName from "../../utils/prettifyDocumentName";
import { ModalFormHeader, FieldLabel } from "../PaperForm";
import { toId, jobOptionsOf, userOptionsOf } from "../../utils/taskSelectOptions";
import OperatorAcknowledgement from "../OperatorAcknowledgement/OperatorAcknowledgement";
import ApprovalSignOff from "../ApprovalSignOff";
import ApiCall from "../../utils/apicall";

// ─── PDF thumbnail helper (mirrors AddTask.jsx) ───────────────────────────────
let _pdfjsLib = null;
async function getPdfjsLib() {
  if (_pdfjsLib) return _pdfjsLib;
  _pdfjsLib = await import("pdfjs-dist/legacy/build/pdf");
  _pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
  return _pdfjsLib;
}
async function pdfPageOneThumbnail(pdfFile) {
  const lib = await getPdfjsLib();
  const arrayBuffer = await pdfFile.arrayBuffer();
  const pdf = await lib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement("canvas");
  canvas.width  = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

const STATUS_OPTIONS = [
  { id: 1, name: "Pending",     value: "pending" },
  { id: 2, name: "Approved",    value: "approved" },
  { id: 3, name: "Rejected",    value: "rejected" },
  { id: 4, name: "In Process",  value: "in_process" },
  { id: 5, name: "To Inspect",  value: "to_inspect" },
];

const EditTask = (props) => {
  const { currentTask, errorValue } = useSelector((state) => state.tasksSlice);
  const dispatch = useDispatch();

  const [data, setData] = useState({
    heatNo: currentTask.heatNo,
    description: currentTask.description,
    comments: currentTask.comments ? currentTask.comments : "",
    projectManager: currentTask.projectManager,
    QCI: currentTask.QCI,
    fitter: currentTask.fitter,
    welder: currentTask.welder,
    painter: currentTask.painter,
    foreman: currentTask.foreman,
  });

  const [status, setSelectedStatus] = useState(currentTask.status);
  const [userId, setSelectedUserId] = useState(toId(currentTask.userId));

  const currentTaskStartDate = currentTask.startedAt ? dayjs(currentTask.startedAt) : null;
  const currentTaskEndDate   = currentTask.completedAt ? dayjs(currentTask.completedAt) : null;

  const [selectedFile, setSelectedFile] = useState(null);
  const [perviewImage, setPerviewImage] = useState(currentTask.image);
  // S-68: the PDF thumbnail preview is a local blob: URL; free it when it is
  // replaced or the dialog closes (it was never revoked before). Server images
  // go through AuthedImage, which manages its own object URLs.
  useEffect(() => {
    const u = perviewImage;
    return () => { if (typeof u === "string" && u.startsWith("blob:")) URL.revokeObjectURL(u); };
  }, [perviewImage]);

  // ── Auto-matched drawing (by PMK) ────────────────────────────────────────
  // Honours V-01 Rev lock: backend returns the frozen rev (not latest); we
  // surface a drift-dot when newer revs exist. See outputs/SFM-drawings-backend-spec.md §2.1.
  const [autoDoc, setAutoDoc]         = useState(null);
  const [autoDrift, setAutoDrift]     = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  useEffect(() => {
    if (currentTask?.image || !currentTask?.id) return;
    let cancelled = false;
    setAutoLoading(true);
    getDrawingForTask(currentTask.id).then((r) => {
      if (cancelled) return;
      setAutoLoading(false);
      if (r.document) {
        setAutoDoc(r.document);
        setAutoDrift(r.drift);
        const url = r.document.file_url || r.document.url;
        if (url) setPerviewImage(url);
      }
    });
    return () => { cancelled = true; };
  }, [currentTask?.id, currentTask?.image]);

  // ── V-02 drawing acknowledgements for this task ──────────────────────────
  // The operator must acknowledge the current approved drawing rev before the
  // task can advance to to_inspect/complete (enforced server-side by requireAck
  // when ENFORCE_V02_ACK is on). We surface the ack panel + the operator's own
  // ack state so the workflow is visible even before the gate is switched on.
  const currentUserId = useSelector(
    (state) => state.userSlice?.loginUser?.decodedToken?.id
  );
  const [acks, setAcks] = useState([]);
  useEffect(() => {
    if (!currentTask?.id || !autoDoc?.id) return;
    let cancelled = false;
    ApiCall.get(`/task/${currentTask.id}/acknowledgements`)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.data?.acknowledgements || [];
        setAcks(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (!cancelled) setAcks([]); });
    return () => { cancelled = true; };
  }, [currentTask?.id, autoDoc?.id]);

  // The ack row for THIS operator + THIS drawing at its CURRENT rev.
  const existingAck = useMemo(() => {
    if (!autoDoc?.id) return null;
    const rev = String(autoDoc.revision || "");
    return (
      acks.find(
        (a) =>
          Number(a.drawing_id) === Number(autoDoc.id) &&
          String(a.rev_snapshot || "") === rev &&
          (currentUserId == null || Number(a.acknowledged_by) === Number(currentUserId))
      ) || null
    );
  }, [acks, autoDoc?.id, autoDoc?.revision, currentUserId]);

  const [diagramFile,    setDiagramFile]    = useState(null);
  const [thumbnailBlob,  setThumbnailBlob]  = useState(null);
  const [thumbLoading,   setThumbLoading]   = useState(false);

  const [jodId, setJobId] = useState(toId(currentTask.jobId));
  // W-25: options as { value: id, label } (the raw /job and /user rows have no `value`).
  const jobOptions = useMemo(() => jobOptionsOf(props.jobs), [props.jobs]);
  const userOptions = useMemo(() => userOptionsOf(props.users), [props.users]);
  const [estimatedHour, setEstimatedHour] = useState(currentTask.estimatedHour);
  const [selectedReasons, setSelectedReasons] = useState(
    currentTask.rejectionReason ? currentTask.rejectionReason : []
  );
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const openFullscreen  = (image) => { setFullscreenImage(image); setIsFullscreenOpen(true); };
  const closeFullscreen = ()      => { setFullscreenImage(null); setIsFullscreenOpen(false); };

  const handleChange = (event) => {
    if (event.target.name === "estimatedHour") {
      setEstimatedHour(event.target.value);
    } else {
      setData((prv) => ({ ...prv, [event.target.name]: event.target.value }));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type === "application/pdf") {
      // New design drawing PDF — re-generate thumbnail client-side.
      // If thumbnail generation fails we clear the PDF rather than letting
      // submit silently send nothing.
      setDiagramFile(file);
      setThumbLoading(true);
      pdfPageOneThumbnail(file)
        .then((blob) => {
          setThumbnailBlob(blob);
          const url = URL.createObjectURL(blob);
          setPerviewImage(url);
        })
        .catch((err) => {
          setDiagramFile(null);
          setThumbnailBlob(null);
          toast.error(
            "Couldn't generate a thumbnail from that PDF. Please try a different file or attach an image instead.",
            { position: toast.POSITION.TOP_RIGHT }
          );
          // eslint-disable-next-line no-console
          console.error("EditTask thumbnail generation failed:", err);
        })
        .finally(() => setThumbLoading(false));
    } else {
      setSelectedFile(file);
      setDiagramFile(null);
      setThumbnailBlob(null);
      const reader = new FileReader();
      reader.onload = (e) => setPerviewImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    // Belt-and-braces guard for silent-attachment-drop:
    if (diagramFile && !thumbnailBlob) {
      toast.error(
        thumbLoading
          ? "Thumbnail is still generating. Please wait a moment and try again."
          : "PDF thumbnail couldn't be generated — re-attach the file or pick a different one.",
        { position: toast.POSITION.TOP_RIGHT }
      );
      return;
    }
    const jobIdInt = toId(jodId);
    if (jobIdInt === null) {
      toast.error("Please select a job", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    // The assignee is optional on the server: an empty choice is left out (the server then stores
    // no assignee); a chosen one is sent as an integer, never as "null"/"undefined".
    const userIdInt = toId(userId);
    if (userId !== null && userId !== undefined && userId !== "" && userIdInt === null) {
      toast.error("Please select the assignee again", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    const formData = new FormData();
    formData.append("heatNo", data.heatNo);
    formData.append("description", data.description);
    formData.append("estimatedHour", Number(estimatedHour));
    formData.append("comments", data.comments);
    formData.append("jobId", String(jobIdInt));
    formData.append("status", status);
    if (userIdInt !== null) formData.append("userId", String(userIdInt));
    formData.append("projectManager", data.projectManager);
    formData.append("QCI", data.QCI);
    formData.append("fitter", data.fitter);
    formData.append("welder", data.welder);
    formData.append("painter", data.painter);
    formData.append("foreman", data.foreman);
    if (thumbnailBlob && diagramFile) {
      formData.append("image",   thumbnailBlob,  "thumbnail.png");
      formData.append("diagram", diagramFile,    diagramFile.name);
    } else if (selectedFile) {
      formData.append("image", selectedFile);
    }
    if (currentTaskStartDate) formData.append("startedAt",   currentTaskStartDate.toISOString());
    if (currentTaskEndDate)   formData.append("completedAt", currentTaskEndDate.toISOString());
    selectedReasons.forEach((value) => { formData.append("rejectionReason", value); });

    dispatch(updateTasks({ formData, id: currentTask.id })).then((res) => {
      if (res.type === "updateTasks/tasks/fulfilled") {
        props.setOpen(false); // the tasks slice shows "Task updated successfully."
      }
      if (res.type === "updateTasks/tasks/rejected") {
        // Keep the dialog open so the operator can act on the error (e.g. a
        // V-02 ack block returns 409) and retry. The reducer surfaces the
        // server's descriptive message, so we don't double-toast here.
        props.setOpen(true);
      }
    });
  };

  const remainingReasons = useMemo(
    () => props.reasons.filter((reason) => !selectedReasons.includes(reason.name)),
    [props.reasons, selectedReasons]
  );
  const handleReasonsChange = (event, newValue) => { setSelectedReasons(newValue); };

  const staffFields = [
    { name: "projectManager", label: "Project Manager" },
    { name: "QCI",            label: "QCI" },
    { name: "fitter",         label: "Fitter" },
    { name: "welder",         label: "Welder" },
    { name: "painter",        label: "Painter" },
    { name: "foreman",        label: "Foreman" },
  ];

  return (
    <Box sx={{ overflowY: selectedFile || perviewImage ? "auto" : "auto", maxHeight: 550, overflowX: "hidden", pr: 0.5 }}>
      <ModalFormHeader
        overline="SFM · Tasks"
        title="Edit Task"
        icon={AssignmentTurnedInOutlined}
      />

      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="heatNo" label="Heat Number"
              defaultValue={data.heatNo}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Job</FieldLabel>
            <CSelect
              label=""
              data={jobOptions}
              jobId={props.data.jobId}
              initialValue={jodId}
              setinitialValue={setJobId}
              margin="0px"
            />
          </Grid>

          <Grid item xs={12}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="description" label="Description"
              defaultValue={data.description}
            />
          </Grid>

          {staffFields.map(({ name, label }) => (
            <Grid item xs={12} sm={6} key={name}>
              <CTextField
                margin="0px" fullWidth onChange={handleChange}
                name={name} label={label}
                defaultValue={data[name]}
              />
            </Grid>
          ))}

          <Grid item xs={12} sm={6}>
            <CTextField
              required={true} margin="0px" fullWidth onChange={handleChange}
              name="estimatedHour" label="Estimated Hour"
              defaultValue={estimatedHour}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Assign To</FieldLabel>
            <CSelect
              key={userId}
              disabled={false}
              label=""
              data={userOptions}
              initialValue={userId}
              setinitialValue={setSelectedUserId}
              margin="0px"
              filter="users"
            />
          </Grid>
        </Grid>

        {/* Task Status block */}
        <Box
          sx={{
            border: "1px solid", borderColor: "divider",
            borderRadius: 2, p: 2, bgcolor: "#fafafa",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: "0.04em", display: "block", mb: 1 }}>
            TASK STATUS
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={status === "rejected" ? 6 : 12}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-label">Select Status</InputLabel>
                <Select
                  fullWidth
                  label="Select Status"
                  labelId="status-label"
                  variant="outlined"
                  sx={{ textAlign: "left" }}
                  value={status}
                  onChange={(event) => setSelectedStatus(event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.id} value={option.value}>{option.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {status === "rejected" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    multiple
                    size="small"
                    options={remainingReasons.map((reason) => reason.name)}
                    getOptionLabel={(option) => option}
                    value={selectedReasons}
                    onChange={handleReasonsChange}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip key={option} label={option} {...getTagProps({ index })} />
                      ))
                    }
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Select Reasons"
                        placeholder="Select Reasons"
                      />
                    )}
                    disabled={status !== "rejected"}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>

          {(status === "approved" || status === "rejected") && (
            <Box sx={{ mt: 2 }}>
              <CTextField
                margin="0px" fullWidth onChange={handleChange}
                name="comments" label="Comment"
                defaultValue={data.comments}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DateTimePicker"]}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%" }}>
                  <DateTimePicker
                    sx={{ width: "100%" }}
                    label="Started At"
                    value={currentTaskStartDate}
                    readOnly
                  />
                  <DateTimePicker
                    sx={{ width: "100%" }}
                    label="Completed At"
                    value={currentTaskEndDate}
                    readOnly
                  />
                </Stack>
              </DemoContainer>
            </LocalizationProvider>
          </Box>

          {/* Approval sign-off (e-sign) — who approved / rejected this task and
              when, for AISC 207-25 §16 traceability. Reads the SAVED task
              (currentTask), not the in-progress status edit. Only shown once the
              task has actually been approved/rejected (avoids noise on open tasks). */}
          {["approved", "rejected"].includes((currentTask.status || "").toLowerCase()) && (
          <Box sx={{ mt: 2 }}>
            <ApprovalSignOff
              status={currentTask.status}
              approvedBy={currentTask.approved_by_name || currentTask.approvedByName}
              approvedPosition={currentTask.approved_by_position || currentTask.approvedByPosition}
              approvedAt={currentTask.approved_at || currentTask.approvedAt}
              rejectedBy={currentTask.rejected_by_name || currentTask.rejectedByName}
              rejectedPosition={currentTask.rejected_by_position || currentTask.rejectedByPosition}
              rejectedAt={currentTask.rejected_at || currentTask.rejectedAt}
              rejectionReason={
                currentTask.notes
                || (Array.isArray(currentTask.rejectionReason)
                  ? currentTask.rejectionReason.join(", ")
                  : currentTask.rejectionReason)
              }
            />
          </Box>
          )}
        </Box>

        {/* Auto-match & drift indicators */}
        {autoDoc && (
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 1.25, py: 0.75,
              bgcolor: autoDrift?.isDrifted ? "#fff4e5" : "#e8f4fd",
              border: `1px solid ${autoDrift?.isDrifted ? "#f1c28b" : "#90caf9"}`,
              borderRadius: 1, fontSize: 12,
            }}
          >
            {autoDrift?.isDrifted && (
              <Box
                aria-label="drift dot — newer revision exists"
                title="A newer revision of this drawing exists on the job. This task is locked to an earlier rev (audit invariant)."
                sx={{
                  display: "inline-block",
                  width: 10, height: 10, borderRadius: "50%",
                  bgcolor: "#ed6c02", flexShrink: 0,
                }}
              />
            )}
            <Typography variant="caption" sx={{ color: "#444" }}>
              Auto-matched drawing: <strong>{prettifyDocumentName(autoDoc)}</strong>
              {autoDoc.revision && <> · Rev <strong>{autoDoc.revision}</strong></>}
              {autoDrift?.isDrifted && <> · newer rev <strong>{autoDrift.latestRev}</strong> available on job</>}
            </Typography>
          </Box>
        )}
        {autoLoading && !perviewImage && (
          <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
            Looking up drawing for this PMK…
          </Typography>
        )}

        {/* V-02 drawing acknowledgement — operator must acknowledge the current
            approved rev before advancing the task to inspection (AISC §16.25). */}
        {autoDoc && (
          <OperatorAcknowledgement
            taskId={currentTask.id}
            drawingId={autoDoc.id}
            drawingRev={autoDoc.revision || ""}
            drawingName={prettifyDocumentName(autoDoc)}
            existingAck={existingAck}
            onAcknowledged={() => {
              setAcks((prev) => [
                {
                  drawing_id: autoDoc.id,
                  rev_snapshot: autoDoc.revision || "",
                  acknowledged_by: currentUserId,
                  acknowledged_at: new Date().toISOString(),
                },
                ...prev,
              ]);
            }}
            sx={{ bgcolor: "#fff" }}
          />
        )}

        {/* Drawing preview + replacement */}
        <Box
          sx={{
            border: "1px solid", borderColor: "divider",
            borderRadius: 2, p: 2, bgcolor: "#fafafa",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <PictureAsPdfOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: "0.04em" }}>
              DESIGN DRAWING (PDF or image)
            </Typography>
          </Stack>

          {perviewImage && (
            <Box sx={{ width: "100%", textAlign: "center", mb: 1 }}>
              {isPdfUrl(perviewImage) ? (
                <AuthedObject
                  src={perviewImage}
                  ariaLabel="task drawing PDF preview"
                  style={{ width: "100%", maxWidth: 700, height: 320, borderRadius: 4 }}
                  onClick={() => openFullscreen(perviewImage)}
                />
              ) : (
                <AuthedImage
                  alt="task drawing preview"
                  style={{ maxWidth: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 4, cursor: "zoom-in" }}
                  src={perviewImage}
                  onClick={() => openFullscreen(perviewImage)}
                />
              )}
            </Box>
          )}

          <CTextField
            name="File"
            type="file"
            onChange={handleFileChange}
            inputProps={{ accept: ".pdf,.png,.jpg,.jpeg,.gif,.webp" }}
            margin="0px"
            fullWidth
          />

          {thumbLoading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Generating thumbnail…
              </Typography>
            </Stack>
          )}
        </Box>

        <Button
          disabled={props.isLoading || thumbLoading}
          onClick={handleSubmit}
          variant="contained"
          sx={{
            width: "100%",
            mt: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.01em",
          }}
        >
          {props.isLoading ? "Loading…" : "Save changes"}
        </Button>
      </Stack>

      {/* Fullscreen modal — preserved */}
      <Modal
        open={isFullscreenOpen}
        onClose={closeFullscreen}
        aria-labelledby="image-modal"
        aria-describedby="image-modal-description"
      >
        <Box sx={{ position: "relative", outline: "none" }}>
          <Button
            onClick={closeFullscreen}
            startIcon={<CloseRounded />}
            variant="contained"
            color="inherit"
            sx={{
              position: "absolute", top: "5%", right: "3%",
              minWidth: 0, borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.92)",
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
              fontWeight: 600,
            }}
          >
            Close
          </Button>

          {isPdfUrl(fullscreenImage) ? (
            <AuthedObject
              src={fullscreenImage}
              ariaLabel="task drawing PDF fullscreen preview"
              style={{ width: "100%", height: "600px", marginTop: "25px" }}
            />
          ) : (
            <AuthedImage
              alt="task drawing fullscreen"
              style={{ width: "100%", height: "600px", marginTop: "25px", objectFit: "contain" }}
              src={fullscreenImage}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default EditTask;
